import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { handlePostRequest } from '../../service/PostTemplate';
import { endPoints } from '../../Config';
import { setUser } from '../../redux/slices/authenticationSlice';

const Documents = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.login.user);

    const [loadingBtn, setLoadingBtn] = useState(false);
    const [fileError, setFileError] = useState('');

    const [files, setFiles] = useState({
        form29: null,
        ntn: null,
        cnicFront: null,
        cnicBack: null,
        profilePic: null
    });

    // Refs for file inputs
    const fileRefs = {
        form29: useRef(null),
        ntn: useRef(null),
        cnicFront: useRef(null),
        cnicBack: useRef(null),
        profilePic: useRef(null)
    };

    const formik = useFormik({
        initialValues: {
            form29: '',
            ntn: '',
            cnicFront: '',
            cnicBack: '',
            profilePic: ''
        },
        onSubmit: async () => {
            if (!user || !user.data?.merchantId) {
                setFileError('User not found. Please login again.');
                return;
            }

            const formData = new FormData();
            formData.append('merchantId', user.data.merchantId);
            formData.append('CreatedBy', user.data.userId);

            Object.keys(files).forEach((key) => {
                if (files[key]) {
                    formData.append(key, files[key]);
                }
            });

            setLoadingBtn(true);
            const response = await dispatch(
                handlePostRequest(formData, endPoints.uploadMerchantDocuments, true)
            );
            setLoadingBtn(false);

            if (response?.status === 200) {
                // ✅ Update Redux and localStorage with isDocuments = true
                const updatedUser = {
                    ...user,
                    data: {
                        ...user.data,
                        isDocuments: true
                    }
                };
                dispatch(setUser(updatedUser));
                localStorage.setItem('user', JSON.stringify(updatedUser));

                formik.resetForm();
                setFiles({
                    form29: null,
                    ntn: null,
                    cnicFront: null,
                    cnicBack: null,
                    profilePic: null
                });

                navigate('/'); // Redirect after successful upload
            }
        }
    });

    const openFileDialog = (field) => {
        fileRefs[field].current.click();
    };

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        const maxSize = 2 * 1024 * 1024; // 2MB

        setFileError('');

        if (!file) return;

        if (!allowedTypes.includes(file.type)) {
            setFileError('Only PNG, JPEG, or PDF allowed.');
            return;
        }

        if (file.size > maxSize) {
            setFileError('File must be under 2MB.');
            return;
        }

        setFiles((prev) => ({ ...prev, [field]: file }));
        formik.setFieldValue(field, file.name);
    };

    const renderFileInput = (label, field) => (
        <div className="col-12 md:col-6" key={field}>
            <div className="mb-3">
                <div className="field">
                    <label>
                        {label} <span style={{ color: 'red' }}>*</span>
                    </label>

                    <div
                        className="file-input-container file-input-wrapper w-100"
                        onClick={() => openFileDialog(field)}
                    >
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            ref={fileRefs[field]}
                            onChange={(e) => handleFileChange(e, field)}
                        />
                        <input
                            type="text"
                            placeholder="Choose a file..."
                            value={formik.values[field]}
                            readOnly
                            className="file-input-field"
                        />
                    </div>

                    {formik.errors[field] && (
                        <small className="p-error">{formik.errors[field]}</small>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="grid p-fluid">
                {renderFileInput('Upload Form 29', 'form29')}
                {renderFileInput('Upload NTN Document', 'ntn')}
                {renderFileInput('Upload CNIC Front', 'cnicFront')}
                {renderFileInput('Upload CNIC Back', 'cnicBack')}
                {renderFileInput('Upload Profile Picture', 'profilePic')}
            </div>

            {fileError && <small className="p-error">{fileError}</small>}

            <div className="mt-3">
                <p>Allowed formats: PNG, JPEG, PDF. Max size: 2MB.</p>
            </div>

            <div className="flex gap-2 mt-4">
                <Button
                    label="Upload Documents"
                    icon="pi pi-upload"
                    type="submit"
                    disabled={loadingBtn}
                    className="p-button-success p-button-sm"
                />
            </div>
        </form>
    );
};

export default Documents;
